import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Format number without currency symbol - one decimal place
const formatNumber = (value: number): string => {
  return value.toLocaleString('he-IL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

export const DashboardPage: React.FC = () => {
  const { t } = useLocale();
  const {
    currentBudget,
    entries,
    projects,
    calculateSummary,
    getPeriodsList,
    selectedPeriod,
    setSelectedPeriod,
    isLoading
  } = useBudget();

  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());

  const summary = useMemo(() => calculateSummary(selectedPeriod || undefined), [calculateSummary, selectedPeriod]);
  // Filter out period "0" which can be created during initialization
  const periods = getPeriodsList().filter(p => p !== '0');

  const chartData = useMemo(() => {
    if (!currentBudget) return [];

    return periods.slice(-6).map(period => {
      const periodSummary = calculateSummary(period);
      return {
        period,
        income: periodSummary.totalIncome,
        expenses: periodSummary.totalExpenses,
        profit: periodSummary.profit,
      };
    });
  }, [currentBudget, periods, calculateSummary]);

  const overviewData = useMemo(() => {
    if (!currentBudget) return [];

    // Get actual measure names from budget (Hebrew: בפועל = Actual, מתוכנן = Budget/Planned)
    const measures = currentBudget.measures || ['בפועל', 'מתוכנן'];
    // First measure is typically 'actual', second is 'budget/planned'
    const actualMeasure = measures[0] || 'בפועל';
    const budgetMeasure = measures[1] || 'מתוכנן';

    // Helper to match measure names (handles both Hebrew and English)
    const isBudgetMeasure = (m: string) => m === budgetMeasure || m === 'budget' || m === 'מתוכנן';
    const isActualMeasure = (m: string) => m === actualMeasure || m === 'actual' || m === 'בפועל';

    // Filter out 'calculated' subjects (like Profit) from the table - they're shown in summary cards
    return currentBudget.subjects
      .filter(subject => subject.type !== 'calculated')
      .map(subject => {
        // Match entries by subject ID or name (fallback when ID lookup failed during parsing)
        const subjectEntries = entries.filter(e =>
          (e.subjectId === subject.id || e.subjectId === subject.name) &&
          (!selectedPeriod || e.period === selectedPeriod)
        );

        const budget = subjectEntries.filter(e => isBudgetMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);
        const actual = subjectEntries.filter(e => isActualMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);
        const goal = subjectEntries.filter(e => e.measure === 'goal').reduce((sum, e) => sum + e.amount, 0);

        // Get sections for this subject
        const sections = currentBudget.sections
          .filter(s => s.subjectId === subject.id)
          .map(section => {
            const sectionEntries = entries.filter(e =>
              ((e as any).section === section.name || e.sectionId === section.id) &&
              (e.subjectId === subject.id || e.subjectId === subject.name) &&
              (!selectedPeriod || e.period === selectedPeriod)
            );

            const secBudget = sectionEntries.filter(e => isBudgetMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);
            const secActual = sectionEntries.filter(e => isActualMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);

            return {
              name: section.name,
              budget: secBudget,
              actual: secActual,
              variance: secBudget - secActual,
              variancePercent: secBudget ? ((secBudget - secActual) / secBudget * 100) : 0,
              incomePartGoal: section.incomePartGoal || 0,
            };
          });

        return {
          id: subject.id,
          subject: subject.name,
          type: subject.type,
          budget,
          actual,
          goal,
          variance: budget - actual,
          variancePercent: budget ? ((budget - actual) / budget * 100) : 0,
          sections,
          hasSections: sections.length > 0,
        };
      });
  }, [currentBudget, entries, selectedPeriod]);

  const toggleExpanded = (subjectId: string) => {
    setExpandedSubjects(prev => {
      const next = new Set(prev);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (!currentBudget) {
    return (
      <div className="empty-state">
        <h2>{t('budget.noBudgets')}</h2>
        <p>{t('budget.createFirst')}</p>
        <Link to="/settings" className="btn btn-primary">
          {t('budget.createBudget')}
        </Link>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>{t('nav.dashboard')}</h1>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <select
            className="period-selector"
            value={selectedPeriod || ''}
            onChange={(e) => setSelectedPeriod(e.target.value || null)}
          >
            <option value="">{t('budget.allPeriods')}</option>
            {periods.map(period => (
              <option key={period} value={period}>{period}</option>
            ))}
          </select>
          <button
            className="btn btn-secondary"
            onClick={() => window.print()}
            title={t('general.print')}
          >
            🖨️ {t('general.print')}
          </button>
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card income">
          <div className="card-label">{t('budget.totalIncome')}</div>
          <div className="card-value amount amount-positive">
            {formatNumber(summary.totalIncome)}
          </div>
        </div>

        <div className="summary-card expenses">
          <div className="card-label">{t('budget.totalExpenses')}</div>
          <div className="card-value amount amount-negative">
            {formatNumber(summary.totalExpenses)}
          </div>
        </div>

        <div className="summary-card profit">
          <div className="card-label">{t('budget.profit')}</div>
          <div className={`card-value amount ${summary.profit >= 0 ? 'amount-positive' : 'amount-negative'}`}>
            {formatNumber(summary.profit)}
          </div>
        </div>

        <div className="summary-card projects">
          <div className="card-label">{t('budget.activeProjects')}</div>
          <div className="card-value">
            {projects.filter(p => p.status === 'active').length}
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="card chart-card">
          <div className="card-header">{t('budget.trend')}</div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Bar dataKey="income" name={t('budget.income')} fill="#22c55e" />
                <Bar dataKey="expenses" name={t('budget.expenses')} fill="#ef4444" />
                <Bar dataKey="profit" name={t('budget.profit')} fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card overview-card">
        <div className="card-header">{t('budget.overview')}</div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>{t('budget.subject')}</th>
                <th>{t('budget.type')}</th>
                <th className="text-end">{t('budget.budget')}</th>
                <th className="text-end">{t('budget.actual')}</th>
                <th className="text-end">{t('budget.variance')}</th>
                <th className="text-end">{t('budget.variancePercent')}</th>
              </tr>
            </thead>
            <tbody>
              {overviewData.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    key={row.id}
                    className={`subject-row ${row.hasSections ? 'expandable' : ''} ${expandedSubjects.has(row.id) ? 'expanded' : ''}`}
                    onClick={() => row.hasSections && toggleExpanded(row.id)}
                  >
                    <td>
                      <span className="subject-cell">
                        {row.hasSections && (
                          <span className="expand-icon">{expandedSubjects.has(row.id) ? '▼' : '▶'}</span>
                        )}
                        <strong>{row.subject}</strong>
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${row.type === 'credit' ? 'badge-success' : 'badge-danger'}`}>
                        {row.type === 'credit' ? t('budget.income') : t('budget.expense')}
                      </span>
                    </td>
                    <td className="text-end amount">{formatNumber(row.budget)}</td>
                    <td className="text-end amount">{formatNumber(row.actual)}</td>
                    <td className={`text-end amount ${row.variance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                      {formatNumber(row.variance)}
                    </td>
                    <td className={`text-end ${row.variancePercent >= 0 ? 'text-success' : 'text-danger'}`}>
                      {row.variancePercent.toFixed(1)}%
                    </td>
                  </tr>
                  {expandedSubjects.has(row.id) && row.sections.map((section, secIdx) => (
                    <tr key={`${row.id}-section-${secIdx}`} className="section-row">
                      <td className="section-name">
                        <span className="section-indent">{section.name}</span>
                        {section.incomePartGoal > 0 && (
                          <span className="goal-badge">{section.incomePartGoal}%</span>
                        )}
                      </td>
                      <td></td>
                      <td className="text-end amount amount-secondary">{formatNumber(section.budget)}</td>
                      <td className="text-end amount amount-secondary">{formatNumber(section.actual)}</td>
                      <td className={`text-end amount amount-secondary ${section.variance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                        {formatNumber(section.variance)}
                      </td>
                      <td className={`text-end amount-secondary ${section.variancePercent >= 0 ? 'text-success' : 'text-danger'}`}>
                        {section.variancePercent.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .dashboard-page {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .page-header h1 {
          font-size: 1.5rem;
          margin: 0;
        }

        .period-selector {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
        }

        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .summary-card {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow);
        }

        .summary-card .card-label {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin-bottom: 0.5rem;
        }

        .summary-card .card-value {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .chart-card, .overview-card {
          margin-bottom: 1.5rem;
        }

        .text-end { text-align: end; }

        .badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .badge-success { background: #dcfce7; color: #166534; }
        .badge-danger { background: #fee2e2; color: #991b1b; }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state h2 {
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          color: var(--color-text-secondary);
          margin-bottom: 1rem;
        }

        .subject-row.expandable {
          cursor: pointer;
        }

        .subject-row.expandable:hover {
          background: var(--color-bg);
        }

        .subject-row.expanded {
          background: var(--color-bg);
        }

        .subject-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .expand-icon {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          width: 1rem;
        }

        .section-row {
          background: #fafafa;
        }

        .section-row td {
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
          font-size: 0.875rem;
        }

        .section-name {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-indent {
          padding-left: 2rem;
          color: var(--color-text-secondary);
        }

        .goal-badge {
          font-size: 0.625rem;
          padding: 0.125rem 0.375rem;
          background: #e3f2fd;
          color: #1565c0;
          border-radius: var(--radius);
        }

        .amount-secondary {
          opacity: 0.8;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};
